import { useDispatch, useSelector } from 'react-redux';
import { ShadowStyle } from 'consts/shadow';
import { changeDealData } from 'store/dealManagement/reducer';
import { Label } from 'lib/Label';
import { Padding } from 'lib/BaseSelect/BaseSelect.types';
import { InputShape, InputSize, InputTheme } from 'components/common/types/Input.types';
import useIsReadOnly from 'components/pages/Planner/useIsReadOnly';
import AsyncSelect from 'lib/AsyncSelect';
import { Store } from 'components/common/types/Store.types';
import { CodeNameModel } from 'components/common/types';
import { isReadOnly } from 'utils/isReadOnly';
import { getIsReseller } from 'store/publisher/selectors';
import { getAgency } from 'store/dealManagement/selectors/getCommonDealDetails';
import { getIsEditingDisabled } from 'store/dealManagement/selectors/getIsEditingDisabled';
import { useLoadLookup } from 'components/common/FilterArea/Filters/useLoadLookup';
import { isRequiredDirectSalesField } from 'utils/isRequiredDirectSalesField';
import Tooltip, { TooltipDirection } from 'lib/Tooltip';
import usePendoTracking from 'customHooks/usePendoTracking';

interface SelectAgencyProps {
  isDisabled?: boolean;
}

const SelectAgency = ({ isDisabled }: SelectAgencyProps): React.ReactElement | null => {
  const dispatch = useDispatch();

  const agency = useSelector(getAgency);
  const isEditingDisabled = useSelector(getIsEditingDisabled);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const directSalesRequiredFieldsConfig = useSelector((state: Store) => state.publisher.directSalesRequiredFields);
  const isRequired = isRequiredDirectSalesField({ fieldsConfig: directSalesRequiredFieldsConfig, fieldName: 'agency' });
  const isReseller = useSelector(getIsReseller);

  const { onLoadLookup } = useLoadLookup();

  const { isCampaignReadOnly } = useIsReadOnly();
  const { getPendoDataId } = usePendoTracking();

  const handleSelectAgency = (key: string, value: CodeNameModel): void => {
    dispatch(changeDealData({ [key]: value }));
  };

  if (isReseller) return null;

  return (
    <>
      <Label id="agency" theme={InputTheme.FLAT_GRAY} label="Agency" isRequired={isRequired} />
      <Tooltip tooltip={agency?.name} direction={TooltipDirection.BOTTOM} isHiddenOnFocus>
        <AsyncSelect
          name="agency"
          dataTestId="async-select-agency"
          placeholder=""
          theme={InputTheme.WHITE_WITH_BORDER}
          size={InputSize.EXTRA_SMALL}
          padding={Padding.NONE}
          shadow={ShadowStyle.NONE}
          shape={InputShape.ROUNDED}
          selectedValue={agency}
          loadData={onLoadLookup}
          onSelect={handleSelectAgency}
          isDisabled={isDisabled || isReadOnly(bookingStatusCode, isEditingDisabled) || isCampaignReadOnly}
          dataPendoId={getPendoDataId('Agency')}
        />
      </Tooltip>
    </>
  );
};

export default SelectAgency;
